import { type FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  hasPasswordMinimumLength,
  hasPasswordMixedCase,
  hasPasswordNumber,
  hasPasswordSpecialCharacters,
  PASSWORD_POLICY_NEW_MIN_LENGTH,
} from '../../passwordRequirementValidation'

import { AuthenticationPasswordRequirementsIndicatorRow } from './AuthenticationPasswordRequirementsIndicatorRow'

interface AuthenticationPasswordRequirementsIndicatorProps {
  password: string
}

export const AuthenticationPasswordRequirementsIndicator: FC<
  AuthenticationPasswordRequirementsIndicatorProps
> = ({ password }) => {
  const checks = useMemo(() => {
    return {
      hasMinimumLength: hasPasswordMinimumLength(
        password,
        PASSWORD_POLICY_NEW_MIN_LENGTH,
      ),
      hasMixedCase: hasPasswordMixedCase(password),
      hasNumber: hasPasswordNumber(password),
      hasSpecialCharacters: hasPasswordSpecialCharacters(password),
    }
  }, [password])

  return (
    <section className="flex flex-col items-start gap-2">
      <h2 className="font-medium text-dark-primary-max">
        <FormattedMessage id="passwordValidation.headline" />
      </h2>
      <ul className="flex flex-col items-start gap-2">
        <AuthenticationPasswordRequirementsIndicatorRow
          isValidationSuccess={checks.hasMinimumLength}
          key="gdpr-hasMinimumLength-indicator"
        >
          <FormattedMessage
            id="passwordValidation.min.characters"
            values={{
              minLength: PASSWORD_POLICY_NEW_MIN_LENGTH,
            }}
          />
        </AuthenticationPasswordRequirementsIndicatorRow>

        <AuthenticationPasswordRequirementsIndicatorRow
          isValidationSuccess={checks.hasMixedCase}
          key="gdpr-hasMixedCase-indicator"
        >
          <FormattedMessage id="passwordValidation.upperLowerCase" />
        </AuthenticationPasswordRequirementsIndicatorRow>

        <AuthenticationPasswordRequirementsIndicatorRow
          isValidationSuccess={checks.hasNumber}
          key="gdpr-hasNumber-indicator"
        >
          <FormattedMessage id="passwordValidation.oneNumber" />
        </AuthenticationPasswordRequirementsIndicatorRow>

        <AuthenticationPasswordRequirementsIndicatorRow
          isValidationSuccess={checks.hasSpecialCharacters}
          key="gdpr-hasSpecialCharacters-indicator"
        >
          <FormattedMessage id="passwordValidation.special" />
        </AuthenticationPasswordRequirementsIndicatorRow>
      </ul>
    </section>
  )
}
